import { X } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import {
  ProfileMentionInput,
  visibleProfileMentions,
} from '@/src/features/posts/ProfileMentionInput';
import { ProfileMentionText } from '@/src/features/posts/ProfileMentionText';
import type {
  ContentEditHistoryEntry,
  ProfileMention,
} from '@/src/features/posts/post-service';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { formatDate } from '@/src/utils/dates';

type MentionScope =
  | { kind: 'post' }
  | { kind: 'comment'; postId: string }
  | { kind: 'vehicle_comment'; vehicleId: string };

function normalizedMentions(mentions: ProfileMention[], value: string) {
  return visibleProfileMentions(mentions, value)
    .map((mention) => ({
      label: mention.label.trim(),
      uid: mention.uid.trim(),
    }))
    .filter((mention) => mention.uid && mention.label)
    .sort((first, second) => {
      const uidDelta = first.uid.localeCompare(second.uid);

      return uidDelta || first.label.localeCompare(second.label);
    });
}

function mentionsChanged({
  current,
  next,
  nextBody,
}: {
  current: ProfileMention[];
  next: ProfileMention[];
  nextBody: string;
}) {
  const currentMentions = normalizedMentions(current, nextBody);
  const nextMentions = normalizedMentions(next, nextBody);

  if (currentMentions.length !== nextMentions.length) {
    return true;
  }

  return currentMentions.some((mention, index) => {
    const nextMention = nextMentions[index];

    return (
      !nextMention ||
      mention.uid !== nextMention.uid ||
      mention.label !== nextMention.label
    );
  });
}

export function EditContentModal({
  allowEmpty,
  body,
  loading,
  maxLength,
  mentions,
  onClose,
  onSave,
  scope,
  title,
  visible,
}: {
  allowEmpty: boolean;
  body: string;
  loading: boolean;
  maxLength: number;
  mentions: ProfileMention[];
  onClose: () => void;
  onSave: (nextBody: string, nextMentions: ProfileMention[]) => void;
  scope: MentionScope;
  title: string;
  visible: boolean;
}) {
  const [draftBody, setDraftBody] = useState(body);
  const [draftMentions, setDraftMentions] = useState<ProfileMention[]>(mentions);
  const trimmedBody = draftBody.trim();
  const hasChanges =
    trimmedBody !== body.trim() ||
    mentionsChanged({
      current: mentions,
      next: draftMentions,
      nextBody: trimmedBody,
    });
  const canSave = Boolean(trimmedBody || allowEmpty) && hasChanges && !loading;

  useEffect(() => {
    if (!visible) {
      return;
    }

    setDraftBody(body);
    setDraftMentions(mentions);
  }, [body, mentions, visible]);

  function save() {
    if (!canSave) {
      return;
    }

    onSave(trimmedBody, visibleProfileMentions(draftMentions, trimmedBody));
  }

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable
          style={styles.modal}
          onPress={(event) => event.stopPropagation()}
        >
          <View style={styles.header}>
            <Text style={styles.title}>{title}</Text>
            <Pressable
              accessibilityLabel="Close editor"
              accessibilityRole="button"
              disabled={loading}
              onPress={onClose}
              style={styles.closeButton}
            >
              <X color={colors.text} size={22} strokeWidth={2.6} />
            </Pressable>
          </View>
          <ProfileMentionInput
            label="Text"
            maxLength={maxLength}
            mentions={draftMentions}
            multiline
            numberOfLines={6}
            placeholder="Write something..."
            scope={scope}
            style={styles.editorInput}
            textAlignVertical="top"
            value={draftBody}
            onChangeText={setDraftBody}
            onMentionsChange={setDraftMentions}
          />
          <Text style={styles.countText}>
            {trimmedBody.length} / {maxLength.toLocaleString('en-US')}
          </Text>
          <View style={styles.actions}>
            <Button
              disabled={loading}
              style={styles.actionButton}
              title="Cancel"
              variant="secondary"
              onPress={onClose}
            />
            <Button
              disabled={!canSave}
              loading={loading}
              style={styles.actionButton}
              title="Save"
              onPress={save}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

export function EditHistoryModal({
  currentUid,
  loadHistory,
  onClose,
  title,
  visible,
}: {
  currentUid?: string;
  loadHistory: () => Promise<ContentEditHistoryEntry[]>;
  onClose: () => void;
  title: string;
  visible: boolean;
}) {
  const [entries, setEntries] = useState<ContentEditHistoryEntry[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;

    if (!visible) {
      setEntries([]);
      setError(null);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setError(null);
    void loadHistory()
      .then((nextEntries) => {
        if (active) {
          setEntries(nextEntries);
        }
      })
      .catch((nextError: Error) => {
        if (active) {
          setError(nextError);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [loadHistory, visible]);

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable
          style={styles.modal}
          onPress={(event) => event.stopPropagation()}
        >
          <View style={styles.header}>
            <Text style={styles.title}>{title}</Text>
            <Pressable
              accessibilityLabel="Close edit history"
              accessibilityRole="button"
              onPress={onClose}
              style={styles.closeButton}
            >
              <X color={colors.text} size={22} strokeWidth={2.6} />
            </Pressable>
          </View>
          {loading ? (
            <View style={styles.statusRow}>
              <ActivityIndicator color={colors.primary} size="small" />
              <Text style={styles.statusText}>Loading history...</Text>
            </View>
          ) : null}
          {!loading && error ? (
            <Text style={styles.errorText}>Unable to load edit history.</Text>
          ) : null}
          {!loading && !error && entries.length === 0 ? (
            <Text style={styles.statusText}>No edits yet.</Text>
          ) : null}
          <ScrollView
            contentContainerStyle={styles.historyList}
            showsVerticalScrollIndicator={false}
          >
            {entries.map((entry) => (
              <View key={entry.id} style={styles.historyEntry}>
                <Text style={styles.historyMeta}>
                  Edit {entry.editNumber}
                  {entry.editedAt ? ` · ${formatDate(entry.editedAt)}` : ''}
                </Text>
                <Text style={styles.historyLabel}>Before</Text>
                {entry.previousBody ? (
                  <ProfileMentionText
                    body={entry.previousBody}
                    currentUid={currentUid}
                    mentions={entry.previousProfileMentions}
                    source="edit_history_previous"
                    style={styles.historyBody}
                  />
                ) : (
                  <Text style={styles.emptyBody}>No text</Text>
                )}
                <Text style={styles.historyLabel}>After</Text>
                {entry.nextBody ? (
                  <ProfileMentionText
                    body={entry.nextBody}
                    currentUid={currentUid}
                    mentions={entry.nextProfileMentions}
                    source="edit_history_next"
                    style={styles.historyBody}
                  />
                ) : (
                  <Text style={styles.emptyBody}>No text</Text>
                )}
              </View>
            ))}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  actionButton: {
    flex: 1,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  backdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(15, 9, 43, 0.45)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.sm,
  },
  closeButton: {
    alignItems: 'center',
    borderRadius: 999,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  countText: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
    textAlign: 'right',
  },
  editorInput: {
    minHeight: 132,
  },
  emptyBody: {
    color: colors.mutedText,
    fontSize: 14,
    fontStyle: 'italic',
  },
  errorText: {
    color: colors.danger,
    fontSize: 14,
    fontWeight: '800',
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  historyBody: {
    color: colors.text,
    fontSize: 14,
    lineHeight: 20,
  },
  historyEntry: {
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.xs,
    padding: spacing.md,
  },
  historyLabel: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  historyList: {
    gap: spacing.md,
    paddingBottom: spacing.sm,
  },
  historyMeta: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
  },
  modal: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    gap: spacing.md,
    maxHeight: '88%',
    maxWidth: 620,
    padding: spacing.md,
    width: '100%',
  },
  statusRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  statusText: {
    color: colors.mutedText,
    fontSize: 14,
    fontWeight: '800',
  },
  title: {
    color: colors.text,
    flex: 1,
    fontSize: 20,
    fontWeight: '900',
    textAlign: 'center',
  },
});
